import { TooltipProvider } from "@/components/ui/tooltip";
import {
  AlignHorizontalSpaceAround,
  AlignVerticalSpaceAround,
  LayoutGrid,
  MessageSquare,
  Pencil,
  Shrink,
  Wand2,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarSubmenu,
} from "../components/toolbar";
import type { SectionLayoutMode } from "../utils/sectionChildLayout";
import { SECTION_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { LineStylePicker, type LineStyle } from "./shared/LineStylePicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

const SECTION_LAYOUTS: {
  key: SectionLayoutMode;
  label: string;
  icon: typeof Wand2;
}[] = [
  { key: "tidy", label: "Tidy up", icon: Wand2 },
  { key: "horizontal", label: "Horizontal", icon: AlignHorizontalSpaceAround },
  { key: "vertical", label: "Vertical", icon: AlignVerticalSpaceAround },
  { key: "tile", label: "Tile Layout", icon: LayoutGrid },
];

interface SectionFloatingToolbarProps {
  sectionColor: string;
  noFill: boolean;
  borderStyle: "solid" | "dashed" | "dotted";
  borderWidth?: number;
  onRename: () => void;
  onColorChange: (color: string) => void;
  onNoFillChange: (noFill: boolean) => void;
  onBorderStyleChange: (style: "solid" | "dashed" | "dotted") => void;
  onBorderWidthChange?: (width: number) => void;
  onLayoutMode: (mode: SectionLayoutMode) => void;
  onResizeToFit?: () => void;
  onPopoverOpenChange?: (isOpen: boolean) => void;
  onChatClick?: () => void;
}

export function SectionFloatingToolbar({
  sectionColor,
  noFill,
  borderStyle,
  borderWidth = 2,
  onRename,
  onColorChange,
  onNoFillChange,
  onBorderStyleChange,
  onBorderWidthChange,
  onLayoutMode,
  onResizeToFit,
  onPopoverOpenChange,
  onChatClick,
}: SectionFloatingToolbarProps) {
  const isColorOpenRef = useRef(false);
  const isLineStyleOpenRef = useRef(false);
  const isLayoutOpenRef = useRef(false);
  const [layoutOpen, setLayoutOpen] = useState(false);

  const notifyOpen = useCallback(
    (open: boolean) => {
      onPopoverOpenChange?.(
        isColorOpenRef.current ||
          isLineStyleOpenRef.current ||
          isLayoutOpenRef.current ||
          open,
      );
    },
    [onPopoverOpenChange],
  );

  const handleColorOpenChange = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleLineStyleOpenChange = useCallback(
    (open: boolean) => {
      isLineStyleOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  const handleLayoutOpenChange = useCallback(
    (open: boolean) => {
      setLayoutOpen(open);
      isLayoutOpenRef.current = open;
      notifyOpen(open);
    },
    [notifyOpen],
  );

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame
        onMouseDown={(e) => e.preventDefault()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {onChatClick && (
          <CanvasToolbarButton
            label="Chat about this"
            accent
            onClick={onChatClick}
          >
            <MessageSquare className="size-4" />
          </CanvasToolbarButton>
        )}

        {onChatClick && <CanvasToolbarDivider />}

        <CanvasToolbarButton
          label="Rename"
          onClick={(e) => {
            e.stopPropagation();
            onRename();
          }}
        >
          <Pencil className="size-4" />
        </CanvasToolbarButton>

        <CanvasColorPickerButton
          colors={SECTION_COLORS}
          role="section"
          activeValue={sectionColor}
          noFill={noFill}
          tooltip="Background"
          onColorChange={onColorChange}
          onNoFillChange={onNoFillChange}
          onOpenChange={handleColorOpenChange}
        />

        <CanvasToolbarSubmenu
          open={layoutOpen}
          onOpenChange={handleLayoutOpenChange}
          side="top"
          trigger={
            <CanvasToolbarButton
              label="Layout"
              dropdown
              active={layoutOpen}
              aria-expanded={layoutOpen}
            >
              <LayoutGrid className="size-4" />
            </CanvasToolbarButton>
          }
        >
          <CanvasToolbarGroup>
            {SECTION_LAYOUTS.map(({ key, label, icon: Icon }) => (
              <CanvasToolbarButton
                key={key}
                label={label}
                onClick={() => {
                  onLayoutMode(key);
                  handleLayoutOpenChange(false);
                }}
              >
                <Icon className="size-4" />
              </CanvasToolbarButton>
            ))}
            {onResizeToFit && (
              <CanvasToolbarButton
                label="Resize to fit"
                onClick={() => {
                  onResizeToFit();
                  handleLayoutOpenChange(false);
                }}
              >
                <Shrink className="size-4" />
              </CanvasToolbarButton>
            )}
          </CanvasToolbarGroup>
        </CanvasToolbarSubmenu>

        <LineStylePicker
          value={{ strokeStyle: borderStyle, strokeWidth: borderWidth }}
          onChange={(ls: LineStyle) => {
            onBorderStyleChange(ls.strokeStyle);
            onBorderWidthChange?.(ls.strokeWidth);
          }}
          onOpenChange={handleLineStyleOpenChange}
          tooltip="Stroke"
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}

/** Derive border color from a section fill value */
export function getSectionBorderColor(fillValue: string): string {
  const paletteMatch = SECTION_COLORS.find((c) => c.value === fillValue);
  if (paletteMatch) return paletteMatch.swatchHex;
  return darkenHex(fillValue, 0.45);
}

function darkenHex(hex: string, ratio: number): string {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return hex;
  const r = Math.round(parseInt(clean.slice(0, 2), 16) * (1 - ratio));
  const g = Math.round(parseInt(clean.slice(2, 4), 16) * (1 - ratio));
  const b = Math.round(parseInt(clean.slice(4, 6), 16) * (1 - ratio));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}
